import { GutachtenAnsicht, GutachtenTyp, ProbenAnsicht } from '@/api/model';
import { Probentyp, ProbentypHelpers } from '@/types/probentyp';

export type ProbenTyp = Probentyp;

class HomeRouten {
    constructor(private kundennummer?: string | null) {}

    private getBasisRoute(): string {
        return ApiRouten.getBasisRoute(this.kundennummer, 'home');
    }

    uebersetzungen(): string {
        // Für die Übersetzungen gibt es keine Route mit der Kundennummern
        return '/home/spa-uebersetzungen';
    }

    dashboard(): string {
        return `${this.getBasisRoute()}/dashboard`;
    }
}

export class ProbenRouten {
    constructor(
        private typ: ProbenTyp,
        private kundennummer?: string | null
    ) {}

    // Hilfsmethode für Basis-Route
    private getBasisRoute(): string {
        return ApiRouten.getBasisRoute(this.kundennummer, this.typ);
    }

    // Hilfsmethode: Wirft Fehler wenn Probentyp Mustervorlage ist
    private throwIfMustervorlagenFreigabe(): void {
        if (this.typ === Probentyp.Mustervorlage) {
            throw new Error('Mustervorlagen können nicht freigegeben werden.');
        }
    }

    liste(): string {
        const typMapping: Record<Probentyp, string> = {
            [Probentyp.Probe]: 'proben',
            [Probentyp.Mustervorlage]: 'mustervorlagen'
        };

        const action = typMapping[this.typ];
        if (!action) {
            ProbentypHelpers.throwUnbekannterProbentypError(this.typ);
        }

        return `${this.getBasisRoute()}/${action}`;
    }

    neu(): string {
        return `${this.getBasisRoute()}/neu`;
    }

    bearbeiten(id: number): string {
        return `${this.getBasisRoute()}/bearbeiten/${id}`;
    }

    loeschen(id: number): string {
        return `${this.getBasisRoute()}/loeschen/${id}`;
    }

    kopieren(id: number): string {
        return `${this.getBasisRoute()}/kopieren/${id}`;
    }

    mischprobeBearbeiten(id: number): string {
        return `${this.getBasisRoute()}/mischprobe-bearbeiten/${id}`;
    }

    testzeitpunktBearbeiten(id: number): string {
        if (this.typ === Probentyp.Mustervorlage) {
            throw new Error('Mustervorlagen haben keinen Testzeitpunkt-Endpunkt.');
        }

        return `${this.getBasisRoute()}/testzeitpunkt-bearbeiten/${id}`;
    }

    freigeben(id: number): string {
        this.throwIfMustervorlagenFreigabe();
        return `${this.getBasisRoute()}/freigeben/${id}`;
    }

    alleFreigeben(): string {
        this.throwIfMustervorlagenFreigabe();
        return `${this.getBasisRoute()}/alle-proben-freigeben`;
    }

    probenFreigeben(): string {
        this.throwIfMustervorlagenFreigabe();
        return `${this.getBasisRoute()}/proben-freigeben`;
    }

    anzahlNichtFreigegebeneProben(): string {
        this.throwIfMustervorlagenFreigabe();
        return `${this.getBasisRoute()}/anzahl-nicht-freigegebene-proben`;
    }

    probennummernReservierungen(): string {
        return `${this.getBasisRoute()}/probennummern-reservierungen`;
    }

    probennummerReservierungLoeschen(probenNr: number): string {
        return `${this.getBasisRoute()}/probennummer-reservierung-loeschen/${probenNr}`;
    }

    excelImportVorschau(): string {
        return `${this.getBasisRoute()}/excel-import-vorschau`;
    }

    excelImportDurchfuehren(): string {
        return `${this.getBasisRoute()}/excel-import-durchfuehren`;
    }

    excelVorlage(): string {
        return `${this.getBasisRoute()}/excel-vorlage`;
    }

    /**
     * Filter-Endpunkt für Mustervorlagen
     * Nur für Probentyp.Mustervorlage sinnvoll – Aufrufer verantworten korrekten Typ.
     */
    filter(): string {
        return `${this.getBasisRoute()}/filter`;
    }
}

class ErgebnisRouten {
    belastungstestAbbrechen(): string {
        return `${this.getBasisRoute()}/belastungstest-abbrechen`;
    }
    constructor(private kundennummer?: string | null) {}

    // Hilfsmethode für Basis-Route
    private getBasisRoute(): string {
        return ApiRouten.getBasisRoute(this.kundennummer, 'ergebnisse');
    }

    endgueltige(): string {
        return `${this.getBasisRoute()}/endgueltige`;
    }

    vorlaeufige(): string {
        return `${this.getBasisRoute()}/vorlaeufige`;
    }

    imLabor(): string {
        return `${this.getBasisRoute()}/im-labor`;
    }

    angemeldete(): string {
        return `${this.getBasisRoute()}/angemeldete`;
    }

    /**
     * Mappt eine ProbenAnsicht auf das Segment der Filter-Route.
     * Beispiel: ImLabor → "im-labor"
     */
    private getApiActionName(ansicht: ProbenAnsicht): string {
        switch (ansicht) {
            case ProbenAnsicht.ImLabor:
                return 'im-labor';
            case ProbenAnsicht.VorlaufigeErgebnisse:
                return 'vorlaeufige';
            case ProbenAnsicht.EndgueltigeErgebnisse:
                return 'endgueltige';
            case ProbenAnsicht.Angemeldete:
                return 'angemeldete';
            default:
                throw new Error(`Filter-Segment für Ansicht "${ansicht}" ist nicht definiert.`);
        }
    }

    /**
     * Generischer Setzen-Endpoint für Ergebnis-Filter
     * POST /ergebnisse/filter/{segment}
     */
    filterSetzen(ansicht: ProbenAnsicht): string {
        const segment = this.getApiActionName(ansicht);
        return `${this.getBasisRoute()}/filter/${segment}`;
    }

    /**
     * Generischer Löschen-Endpoint für Ergebnis-Filter
     * DELETE /ergebnisse/filter/{segment}
     */
    filterLoeschen(ansicht: ProbenAnsicht): string {
        const segment = this.getApiActionName(ansicht);
        return `${this.getBasisRoute()}/filter/${segment}`;
    }

    /**
     * Gibt den API-Endpunkt basierend auf der ProbenAnsicht zurück
     */
    getEndpunkt(probenAnsicht: ProbenAnsicht): string {
        switch (probenAnsicht) {
            case ProbenAnsicht.EndgueltigeErgebnisse:
                return this.endgueltige();
            case ProbenAnsicht.VorlaufigeErgebnisse:
                return this.vorlaeufige();
            case ProbenAnsicht.ImLabor:
                return this.imLabor();
            case ProbenAnsicht.Angemeldete:
                return this.angemeldete();
            default:
                throw new Error(`Unbekannte ProbenAnsicht für Ergebnisse: ${probenAnsicht}`);
        }
    }

    excelExport(): string {
        return `${this.getBasisRoute()}/excel-export`;
    }

    csvExport(): string {
        return `${this.getBasisRoute()}/csv-export`;
    }

    endgueltigePruefberichteDownload(): string {
        return `${this.getBasisRoute()}/endgueltige-pruefberichte`;
    }

    details(probenNr: number, ansicht: ProbenAnsicht): string {
        return `${this.getBasisRoute()}/details/${probenNr}?ansicht=${encodeURIComponent(ansicht)}`;
    }

    pruefberichteDownload(probenNr: number, ansicht: ProbenAnsicht): string {
        return `${this.getBasisRoute()}/details/${probenNr}/pruefberichte-download?ansicht=${encodeURIComponent(ansicht)}`;
    }

    updateDetailLayout(): string {
        return `${this.getBasisRoute()}/update-detail-layout`;
    }

    schnellsuche(probenNr: number): string {
        return `${this.getBasisRoute()}/schnellsuche/${probenNr}`;
    }

    statistikFilter(): string {
        return `${this.getBasisRoute()}/statistik`;
    }

    statistikKuchendiagramm(): string {
        return `${this.getBasisRoute()}/statistik-kuchendiagramm`;
    }

    statistikProbenliste(): string {
        return `${this.getBasisRoute()}/statistik-probenliste`;
    }
}

class SucheRouten {
    constructor(private kundennummer?: string) {}

    private getBasisRoute(): string {
        return ApiRouten.getBasisRoute(this.kundennummer, 'suche');
    }

    suchen(suchtext: string): string {
        return `${this.getBasisRoute()}?suchtext=${encodeURIComponent(suchtext)}`;
    }
}

class TextvorlagenRouten {
    constructor(private kundennummer?: string | null) {}

    private getBasisRoute(): string {
        return ApiRouten.getBasisRoute(this.kundennummer, 'textvorlagen');
    }

    liste(feldname: string, probenID?: number): string {
        let url = `${this.getBasisRoute()}/textvorlagen?feldname=${encodeURIComponent(feldname)}`;

        if (probenID !== undefined) {
            url += `&probenID=${encodeURIComponent(probenID)}`;
        }

        return url;
    }

    erstellen(): string {
        return `${this.getBasisRoute()}/neu`;
    }

    aktualisieren(): string {
        return `${this.getBasisRoute()}/bearbeiten`;
    }

    loeschen(id: number): string {
        return `${this.getBasisRoute()}/loeschen/${id}`;
    }
}

class PruefplanRouten {
    constructor(private kundennummer?: string | null) {}

    private getBasisRoute(): string {
        return ApiRouten.getBasisRoute(this.kundennummer, 'pruefplan');
    }

    pruefplaene(): string {
        return `${this.getBasisRoute()}/pruefplaene`;
    }

    pruefplanPositionen(): string {
        return `${this.getBasisRoute()}/pruefplan-positionen`;
    }
}

class EtikettendruckRouten {
    constructor(private kundennummer?: string | null) {}

    private getBasisRoute(): string {
        return ApiRouten.getBasisRoute(this.kundennummer, 'etikettendruck');
    }

    proben(): string {
        return `${this.getBasisRoute()}/etiketten/proben`;
    }

    etiketten(): string {
        return `${this.getBasisRoute()}/etiketten`;
    }

    probennummernReservierungsVorgaenge(): string {
        return `${this.getBasisRoute()}/etiketten/probennummern-reservierungs-vorgaenge`;
    }

    etikettenDrucken(): string {
        return `${this.getBasisRoute()}/etiketten/drucken`;
    }

    etikettenVordrucken(): string {
        return `${this.getBasisRoute()}/etiketten/vordrucken`;
    }

    etikettenErneutDrucken(): string {
        return `${this.getBasisRoute()}/etiketten/erneut-drucken`;
    }

    filter(): string {
        return `${this.getBasisRoute()}/etiketten/filter`;
    }

    probenbegleitscheinProben(): string {
        return `${this.getBasisRoute()}/probenbegleitschein/proben`;
    }

    probenbegleitscheinFilter(): string {
        return `${this.getBasisRoute()}/probenbegleitschein/filter`;
    }

    probenbegleitscheinDrucken(): string {
        return `${this.getBasisRoute()}/probenbegleitschein/drucken`;
    }
}

class QualitaetskontrollzertifikatRouten {
    constructor(private kundennummer?: string | null) {}

    private getBasisRoute(): string {
        return ApiRouten.getBasisRoute(this.kundennummer, 'qualitaetskontrollzertifikat');
    }

    zertifikate(): string {
        return `${this.getBasisRoute()}/zertifikate`;
    }

    zertifikatDownload(): string {
        return `${this.getBasisRoute()}/zertifikat-download`;
    }
}

class DokumentRouten {
    constructor(private kundennummer?: string | null) {}

    private getBasisRoute(): string {
        return ApiRouten.getBasisRoute(this.kundennummer, 'dokumente');
    }

    probeDokumente(probenNr: number): string {
        return `${this.getBasisRoute()}/probe/${probenNr}`;
    }

    probeDokumentHochladen(probenNr: number): string {
        return this.probeDokumente(probenNr);
    }

    probeDokumentLoeschen(probenNr: number, dokument: string): string {
        return `${this.probeDokumente(probenNr)}/${encodeURIComponent(dokument)}`;
    }

    probe(probenNr: number, dokument: string): string {
        return this.probeDokumentLoeschen(probenNr,
            dokument);
    }

    begehungsbericht(id?: number): string {
        return `${this.getBasisRoute()}/begehungsbericht/${id}`;
    }

    massnahmenplan(id?: number): string {
        return `${this.getBasisRoute()}/massnahmenplan/${id}`;
    }

    begehungsberichtBilder(id?: number): string {
        return `${this.getBasisRoute()}/begehungsbericht-bilder/${id}`;
    }

    gutachten(id: number, dokument: string): string {
        return this.gutachtenDokumentLoeschen(id,
            dokument);
    }

    gutachtenVersion(id: number, dokument: string): string {
        return `${this.getBasisRoute()}/gutachten-version/${id}/${encodeURIComponent(dokument)}`;
    }

    gutachtenDokumente(id: number): string {
        return `${this.getBasisRoute()}/gutachten/${id}`;
    }

    gutachtenDokumentHochladen(id: number): string {
        return this.gutachtenDokumente(id);
    }

    gutachtenDokumentLoeschen(id: number, dokument: string): string {
        return `${this.gutachtenDokumente(id)}/${encodeURIComponent(dokument)}`;
    }
}

class BildRouten {
    constructor(private kundennummer?: string | null) {}

    private getBasisRoute(): string {
        return ApiRouten.getBasisRoute(this.kundennummer, 'bilder');
    }

    probe(probenNr: number, bild: string, scalingName?: string): string {
        let url = `${this.getBasisRoute()}/probe/${probenNr}/${bild}`;

        if (scalingName) {
            url += '?scalingName=' + encodeURIComponent(scalingName);
        }

        return url;
    }

    checklistenFrage(frageId: number, bild: string, scalingName?: string): string {
        let url = `${this.getBasisRoute()}/checklisten-frage/${frageId}/${bild}`;

        if (scalingName) {
            url += '?scalingName=' + encodeURIComponent(scalingName);
        }

        return url;
    }
}

class BegehungsberichteRouten {
    constructor(private kundennummer?: string | null) {}

    private getBasisRoute(): string {
        return ApiRouten.getBasisRoute(this.kundennummer, 'begehungsberichte');
    }

    liste(): string {
        return `${this.getBasisRoute()}/begehungsberichte`;
    }

    details(id: number): string {
        return `${this.getBasisRoute()}/begehungsbericht/${id}`;
    }
}

class WissensdatenbankRouten {
    constructor(private kundennummer?: string | null) {}

    private getBasisRoute(): string {
        return ApiRouten.getBasisRoute(this.kundennummer, 'wissensdatenbank');
    }

    elemente(): string {
        return `${this.getBasisRoute()}/elemente`;
    }
}

export class ApiRouten {
    static readonly AUTHENTIFIZIERUNG = {
        LOGIN: '/konto/login',
        REFRESH: '/konto/refresh',
        LOGOUT: '/konto/logout',
        KENNWORTAENDERN: '/konto/kennwort-aendern',
        BAV_LIMS_ANMELDEN: '/konto/bav-lims-anmelden'
    } as const;

    static readonly KUNDENBEREICH = {
        FILIALEN: '/kundenbereich/filialen',
        EINSTELLUNGEN: '/kundenbereich/einstellungen',
        LAYOUTAKTUALISIEREN: 'kundenbereich/layout-aktualisieren',
        STANDARDLAYOUTWIEDERHERSTELLEN: 'kundenbereich/standard-layout-wiederherstellen',
        EIGENSCHAFTENBEZEICHNUNGEN: 'kundenbereich/eigenschaften-bezeichnungen',
        EIGENSCHAFTENBEZEICHNUNGEN_AKTUALISIEREN: 'kundenbereich/eigenschaften-bezeichnungen-aktualisieren',
        EXCELEXPORTOPTIONENPARAMTER: 'kundenbereich/excel-export-optionen-parameter',
        EXCELEXPORTOPTIONENPARAMTERAKTUALISIEREN: 'kundenbereich/excel-export-optionen-parameter-aktualisieren',
        MIBIMOEGLICHEURSACHENNUTZUNGSBEDINGUNGENBESTAETIGEN: '/kundenbereich/mibi-moegliche-ursachen-nutzungsbedingungen-bestaetigen',
        NUTZUNGSBEDINGUNGEN_BESTAETIGEN: '/kundenbereich/nutzungsbedingungen-bestaetigen'
    } as const;

    // Gemeinsame Hilfsmethode für Basis-Route
    static getBasisRoute(kunde: string | null | undefined, controller: string): string {
        let route = controller;

        if (kunde !== null && kunde !== undefined) {
            route = kunde + '/' + controller;
        }

        return route;
    }

    // Factory-Methoden zum Erstellen von HomeRouten-Instanzen
    static home(kundennummer?: string | null): HomeRouten {
        return new HomeRouten(kundennummer);
    }

    // Factory-Methoden zum Erstellen von ProbenRouten-Instanzen
    static proben(kundennummer?: string | null): ProbenRouten {
        return new ProbenRouten(Probentyp.Probe, kundennummer);
    }

    static mustervorlagen(kundennummer?: string | null): ProbenRouten {
        return new ProbenRouten(Probentyp.Mustervorlage, kundennummer);
    }

    // Factory-Methode zum Erstellen von ErgebnisRouten-Instanzen
    static ergebnisse(kundennummer?: string | null): ErgebnisRouten {
        return new ErgebnisRouten(kundennummer);
    }

    static suche(kundennummer?: string): SucheRouten {
        return new SucheRouten(kundennummer);
    }

    // Factory-Methode zum Erstellen von TextvorlagenRouten-Instanzen
    static textvorlagen(kundennummer?: string | null): TextvorlagenRouten {
        return new TextvorlagenRouten(kundennummer);
    }

    // Factory-Methode zum Erstellen von PruefplanRouten-Instanzen
    static pruefplan(kundennummer?: string | null): PruefplanRouten {
        return new PruefplanRouten(kundennummer);
    }

    static etikettendruck(kundennummer?: string | null): EtikettendruckRouten {
        return new EtikettendruckRouten(kundennummer);
    }

    // Factory-Methode zum Erstellen von QualitaetskontrollzertifikatRouten-Instanzen
    static qualitaetskontrollzertifikat(kundennummer?: string | null): QualitaetskontrollzertifikatRouten {
        return new QualitaetskontrollzertifikatRouten(kundennummer);
    }

    static dokumente(kundennummer?: string | null): DokumentRouten {
        return new DokumentRouten(kundennummer);
    }

    static bilder(kundennummer?: string | null): BildRouten {
        return new BildRouten(kundennummer);
    }

    static begehungsberichte(kundennummer?: string | null): BegehungsberichteRouten {
        return new BegehungsberichteRouten(kundennummer);
    }

    static gutachten(kundennummer?: string | null): GutachtenRouten {
        return new GutachtenRouten(kundennummer);
    }

    static kundenbereichBadges(kundennummer?: string | null) {
        return `${ApiRouten.getBasisRoute(kundennummer, 'kundenbereich')}/badges`;
    }

    static wissensdatenbank(kundennummer?: string | null) {
        return new WissensdatenbankRouten(kundennummer);
    }
}

class GutachtenRouten {
    constructor(private kundennummer?: string | null) {}

    private getBasisRoute(): string {
        return ApiRouten.getBasisRoute(this.kundennummer, 'gutachten');
    }

    erfasste(): string {
        return `${this.getBasisRoute()}/erfasste`;
    }

    neu(gutachtenTypID: GutachtenTyp): string {
        return `${this.getBasisRoute()}/neu?gutachtenTypID=${gutachtenTypID}`;
    }

    bearbeiten(id: number): string {
        return `${this.getBasisRoute()}/bearbeiten/${id}`;
    }

    loeschen(id: number): string {
        return `${this.getBasisRoute()}/loeschen/${id}`;
    }

    freigeben(id: number): string {
        return `${this.getBasisRoute()}/freigeben/${id}`;
    }

    alleFreigeben(): string {
        return `${this.getBasisRoute()}/alle-gutachten-freigeben`;
    }

    anzahlNichtFreigegebeneGutachten(): string {
        return `${this.getBasisRoute()}/anzahl-nicht-freigegebene-gutachten`;
    }

    liste(): string {
        return `${this.getBasisRoute()}/ergebnisse`;
    }

    inBearbeitung(): string {
        return `${this.getBasisRoute()}/in-bearbeitung`;
    }

    rohstoffspezifikationen(): string {
        return `${this.getBasisRoute()}/rohstoffspezifikationen`;
    }

    filter(): string {
        return `${this.getBasisRoute()}/filter/ergebnisse`;
    }

    details(id: number, ansicht: GutachtenAnsicht): string {
        return `${this.getBasisRoute()}/details/${id}?ansicht=${encodeURIComponent(ansicht)}`;
    }
}
